import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Building2, MapPin, Briefcase, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './new-entry-popup.css';

// Entry types
interface NewCandidateEntry {
  type: 'candidate';
  id: string;
  first_name: string;
  last_name: string;
  current_position?: string;
  city?: string;
  created_by_name?: string;
}

interface NewCompanyEntry {
  type: 'company';
  id: string;
  name: string;
  industry?: string;
  billing_city?: string;
  created_by_name?: string;
}

export type NewEntry = NewCandidateEntry | NewCompanyEntry;

interface NewEntryPopupProps {
  entry: NewEntry | null;
  onClose: () => void;
  autoHideDelay?: number; // milliseconds, default 10000 (10 seconds)
}

const NewEntryPopup: React.FC<NewEntryPopupProps> = ({
  entry,
  onClose,
  autoHideDelay = 10000
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (entry) {
      setIsVisible(true);

      // Auto-hide after delay
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDelay);

      return () => clearTimeout(timer);
    }
  }, [entry, autoHideDelay, handleClose]);

  const handleView = () => {
    if (!entry) return;

    if (entry.type === 'candidate') {
      // Navigate to candidates page and maybe open the detail modal
      navigate('/recruiting', { state: { openCandidateId: entry.id } });
    } else {
      // Navigate to companies page
      navigate('/companies', { state: { openCompanyId: entry.id } });
    }
    handleClose();
  };

  if (!entry) return null;

  const isCandidate = entry.type === 'candidate';
  const title = isCandidate ? 'Neuer Kandidat' : 'Neues Unternehmen';
  const name = isCandidate
    ? `${(entry as NewCandidateEntry).first_name} ${(entry as NewCandidateEntry).last_name}`
    : (entry as NewCompanyEntry).name;
  const subtitle = isCandidate
    ? (entry as NewCandidateEntry).current_position
    : (entry as NewCompanyEntry).industry;
  const location = isCandidate
    ? (entry as NewCandidateEntry).city
    : (entry as NewCompanyEntry).billing_city;

  const content = (
    <AnimatePresence>
      {isVisible && (
        <div className="new-entry-popup-container">
          <motion.div
            initial={{ opacity: 0, x: 100, y: -20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100, scale: 0.9 }}
            className="new-entry-popup-card"
          >
            {/* Header */}
            <div className={`new-entry-popup-header ${isCandidate ? 'candidate' : 'company'}`}>
              <div className="new-entry-popup-header-left">
                <div className="new-entry-popup-icon">
                  {isCandidate ? <User size={20} /> : <Building2 size={20} />}
                </div>
                <span className="new-entry-popup-title">{title}</span>
              </div>
              <button onClick={handleClose} className="new-entry-popup-close-btn">
                <X size={16} />
              </button>
            </div>

            {/* Content */}
            <div className="new-entry-popup-content">
              <div className="new-entry-popup-name">{name}</div>

              {subtitle && (
                <div className="new-entry-popup-detail">
                  <Briefcase size={14} />
                  <span>{subtitle}</span>
                </div>
              )}

              {location && (
                <div className="new-entry-popup-detail">
                  <MapPin size={14} />
                  <span>{location}</span>
                </div>
              )}

              {entry.created_by_name && (
                <div className="new-entry-popup-created-by">
                  Erstellt von: {entry.created_by_name}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="new-entry-popup-footer">
              <button onClick={handleView} className="new-entry-popup-view-btn">
                <Eye size={16} />
                <span>Anzeigen</span>
              </button>
            </div>

            {/* Progress bar for auto-hide */}
            <motion.div
              className="new-entry-popup-progress"
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: autoHideDelay / 1000, ease: 'linear' }}
            />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default NewEntryPopup;
